import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from "../environments/environment";

interface PermissionData {
  resource: string;
  actions: string[];
}

/**
 * The boundaries a delegated user manager operates within (SPAR-745).
 *
 * Used to filter the role and division pickers so a manager is not offered options the API would
 * reject. `divisionIds` is null when the manager is exempt from division filtering, which is not
 * the same as an empty array — empty means they hold none and may grant none.
 */
export interface DelegatedUserManagementScope {
  emailDomain: string | null;
  divisionIds: number[] | null;
  assignableRoleIds: number[];
}

interface PermissionsResponse {
  permissions: PermissionData[];
  roles: string[];
  requiresDivisionFiltering: boolean;
  delegatedUserManagement: DelegatedUserManagementScope | null;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionsMap: Map<string, Set<string>> = new Map();
  private permissionsLoaded = new BehaviorSubject<boolean>(false);
  private requiresDivisionFilteringValue: boolean = true; // Default to true (most restrictive)
  private delegatedScopeValue: DelegatedUserManagementScope | null = null;
  private readonly STORAGE_KEY = 'userPermissions';

  constructor(private http: HttpClient) {
    // Try to load cached permissions from localStorage on service initialization
    this.loadCachedPermissions();
  }

  /**
   * Load permissions from localStorage cache
   */
  private loadCachedPermissions(): void {
    try {
      const cached = localStorage.getItem(this.STORAGE_KEY);
      if (cached) {
        const data: PermissionsResponse = JSON.parse(cached);
        this.setPermissions(data.permissions);
        // Load requiresDivisionFiltering from cache, default to true if missing
        this.requiresDivisionFilteringValue = data.requiresDivisionFiltering ?? true;
        // Absent in a cache written before SPAR-745 — treat as "not a delegated manager".
        this.delegatedScopeValue = data.delegatedUserManagement ?? null;
      }
    } catch (error) {
      console.error('Error loading cached permissions:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Save permissions to localStorage cache
   */
  private cachePermissions(data: PermissionsResponse): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error caching permissions:', error);
    }
  }

  /**
   * Set permissions in memory
   */
  private setPermissions(permissions: PermissionData[]): void {
    this.permissionsMap.clear();
    permissions.forEach(perm => {
      this.permissionsMap.set(perm.resource, new Set(perm.actions));
    });
    this.permissionsLoaded.next(true);
  }

  /**
   * Load permissions from the backend API
   */
  loadPermissions(): Observable<PermissionsResponse> {
    return this.http.get<PermissionsResponse>(`${environment.apiUrl}/users/me/permissions`)
      .pipe(
        tap(data => {
          // Store requiresDivisionFiltering, default to true if missing
          this.requiresDivisionFilteringValue = data.requiresDivisionFiltering ?? true;
          this.delegatedScopeValue = data.delegatedUserManagement ?? null;
          // Then set the permissions
          this.setPermissions(data.permissions);
          this.cachePermissions(data);
        })
      );
  }

  /**
   * Clear all permissions (e.g., on logout)
   */
  clearPermissions(): void {
    this.permissionsMap.clear();
    this.permissionsLoaded.next(false);
    this.requiresDivisionFilteringValue = true; // Reset to default (most restrictive)
    this.delegatedScopeValue = null;
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Check if permissions have been loaded
   */
  arePermissionsLoaded(): boolean {
    return this.permissionsLoaded.value;
  }

  /**
   * Get an observable for permissions loaded status
   */
  getPermissionsLoadedObservable(): Observable<boolean> {
    return this.permissionsLoaded.asObservable();
  }

  /**
   * Check if user has a specific permission
   * @param resource Resource name (e.g., 'project', 'invoice')
   * @param action Action name (e.g., 'view', 'create', 'edit', 'delete')
   * @returns true if user has permission, false otherwise
   */
  hasPermission(resource: string, action: string): boolean {
    const resourcePermissions = this.permissionsMap.get(resource.toLowerCase());
    if (!resourcePermissions) {
      return false;
    }
    return resourcePermissions.has(action.toLowerCase());
  }

  /**
   * Check if user can view a resource
   */
  canView(resource: string): boolean {
    return this.hasPermission(resource, 'view');
  }

  /**
   * Check if user can create a resource
   */
  canCreate(resource: string): boolean {
    return this.hasPermission(resource, 'create');
  }

  /**
   * Check if user can edit a resource
   */
  canEdit(resource: string): boolean {
    return this.hasPermission(resource, 'edit');
  }

  /**
   * Check if user can delete a resource
   */
  canDelete(resource: string): boolean {
    return this.hasPermission(resource, 'delete');
  }

  /**
   * Check if user can impersonate other users
   * Requires 'all' permission on user resource (admin only)
   */
  canImpersonate(): boolean {
    // Check if user has 'all' permission on user resource
    const resourcePermissions = this.permissionsMap.get('user');
    if (!resourcePermissions) {
      return false;
    }
    // 'all' action grants all permissions including impersonation
    return resourcePermissions.has('view') && 
           resourcePermissions.has('create') && 
           resourcePermissions.has('edit') && 
           resourcePermissions.has('delete');
  }

  /**
   * Get all actions allowed for a resource
   * @param resource Resource name
   * @returns Array of allowed action names
   */
  getResourceActions(resource: string): string[] {
    const resourcePermissions = this.permissionsMap.get(resource.toLowerCase());
    if (!resourcePermissions) {
      return [];
    }
    return Array.from(resourcePermissions);
  }

  /**
   * Get all resources user has any permission for
   * @returns Array of resource names
   */
  getAllResources(): string[] {
    return Array.from(this.permissionsMap.keys());
  }

  /**
   * Get requiresDivisionFiltering value
   * Returns the cached value. This should be used after permissions are loaded.
   * If permissions haven't been loaded yet, returns true (most restrictive default).
   * @returns boolean indicating if user requires division filtering
   */
  getRequiresDivisionFiltering(): boolean {
    return this.requiresDivisionFilteringValue;
  }

  /**
   * The delegated user-management boundaries for the current user (SPAR-745), or null if they are
   * not a delegated manager. Sparq staff are always null — they manage users through `user:*`.
   */
  getDelegatedScope(): DelegatedUserManagementScope | null {
    return this.delegatedScopeValue;
  }

  /** Whether the current user manages users by delegation rather than as Sparq staff. */
  isDelegatedUserManager(): boolean {
    return this.delegatedScopeValue !== null;
  }

  /**
   * Can the current user reach the user-management screens at all — as Sparq staff or by
   * delegation? The backend enforces the boundaries either way; this only drives what is shown.
   */
  canManageUsers(): boolean {
    return this.canView('user') || this.isDelegatedUserManager();
  }
}
